/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackProps, Stack } from '@fluentui/react/lib/Stack';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { LuisText } from 'src/core/components/LuisText';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylistV2 } from 'src/core/themes/luisStylist';
import {
    ConversationFeaturePickerContainer,
    ConversationFeaturePickerContainerProps
} from 'src/luis/verticals/conversationFeaturePicker/ConversationFeaturePickerContainer';

const messages = defineMessages({
    featuresTitle: {
        id: 'luis.features.featuresSection.featuresTitle',
        defaultMessage: 'Machine learning features'
    },
    featuresDescription: {
        id: 'luis.features.featuresSection.featuresDescription',
        defaultMessage: `In machine learning, a feature is a characteristic that helps a machine
        learned model recognize a concept. You can use entities or phrase lists as features for an intent`
    }
});

const sectionTheme = getScopedTheme('FeatureSection');
const { styleComponent } = getStylistV2('FeatureSection');

const Root = styleComponent<IStackProps>(Stack)('Root', theme => ({
    lineHeight: theme.lineHeight
}));

type FeatureSectionProps = {
    /**
     * The props of the feature picker.
     */
    featurePickerProps: ConversationFeaturePickerContainerProps;

    /**
     * Indicator to display the section horizontally or vertically.
     */
    horizontal?: boolean;
};

/**
 * Displays the feature section in the entity or intent pages.
 */
export const FeatureSection = (props: FeatureSectionProps) => {
    const { horizontal, featurePickerProps } = props;
    const { localize } = useLocalization();

    return (
        <Root verticalAlign="start" horizontal={horizontal} tokens={{ childrenGap: 20 }}>
            <Stack verticalAlign="center" horizontal tokens={{ childrenGap: 5 }}>
                <LuisText variant="large" fontWeight={sectionTheme.fontWeight as Property.FontWeight}>
                    {localize(messages.featuresTitle)}
                </LuisText>

                <CalloutButton content={localize(messages.featuresDescription)} />
            </Stack>

            <ConversationFeaturePickerContainer telemetryContext="FeatureSection" {...featurePickerProps} />
        </Root>
    );
};
